import { useState, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ProjectGalleryProps {
  images: string[];
  imageAlt: string;
  objectFit?: "cover" | "contain";
}

export default function ProjectGallery({
  images,
  imageAlt,
  objectFit = "cover",
}: ProjectGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const goToImage = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(deltaX) > 50) {
        if (deltaX > 0) goToPrevious();
        else goToNext();
      }
      touchStartX.current = null;
    },
    [goToPrevious, goToNext],
  );

  if (!images || images.length === 0) return null;

  // If only one image, just display it without gallery controls
  if (images.length === 1) {
    return (
      <div className="relative rounded-xs overflow-hidden border border-border shadow-lg group">
        <img
          src={images[0]}
          alt={imageAlt}
          className={`w-full h-[280px] xs:h-[340px] sm:h-[460px] lg:h-[560px] ${
            objectFit === "contain"
              ? "object-contain bg-[#002b5c]/5"
              : "object-cover"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
        <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 text-white text-sm sm:text-base lg:text-lg font-body italic">
          {imageAlt}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      {/* Main Image Container */}
      <div
        className="relative rounded-xs overflow-hidden border border-border shadow-lg group"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Image */}
        <img
          src={images[currentIndex]}
          alt={`${imageAlt} - Image ${currentIndex + 1}`}
          className={`w-full h-[280px] xs:h-[340px] sm:h-[460px] lg:h-[560px] transition-opacity duration-300 ${
            objectFit === "contain"
              ? "object-contain bg-[#002b5c]/5"
              : "object-cover"
          }`}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />

        {/* Caption */}
        <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 text-white text-sm sm:text-base lg:text-lg font-body italic">
          {imageAlt}
        </div>

        {/* Previous Button - Left side */}
        <button
          onClick={goToPrevious}
          className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-11 sm:h-11 flex items-center justify-center bg-[#002b5c]/90 hover:bg-[#ffcc29] text-white hover:text-[#002b5c] rounded-full transition-all duration-200 shadow-xl backdrop-blur-sm z-10 active:scale-90 sm:group-hover:scale-110"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6 sm:w-5 sm:h-5" strokeWidth={2.5} />
        </button>

        {/* Next Button - Right side */}
        <button
          onClick={goToNext}
          className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-11 sm:h-11 flex items-center justify-center bg-[#002b5c]/90 hover:bg-[#ffcc29] text-white hover:text-[#002b5c] rounded-full transition-all duration-200 shadow-xl backdrop-blur-sm z-10 active:scale-90 sm:group-hover:scale-110"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6 sm:w-5 sm:h-5" strokeWidth={2.5} />
        </button>

        {/* Image Counter Badge */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-black/60 backdrop-blur-sm text-white font-heading font-bold text-xs sm:text-sm px-3 py-1.5 rounded-full tracking-wider">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => goToImage(idx)}
              className={`shrink-0 relative rounded-xs overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                idx === currentIndex
                  ? "border-[#ffcc29] ring-2 ring-[#ffcc29]/30 scale-[1.02]"
                  : "border-border hover:border-[#002b5c]/50 opacity-80 hover:opacity-100"
              }`}
              style={{ width: "72px", height: "54px" }}
              aria-label={`Go to image ${idx + 1}`}
            >
              <img
                src={img}
                alt={`Thumbnail ${idx + 1}`}
                className={`w-full h-full ${objectFit === "contain" ? "object-contain bg-[#002b5c]/5" : "object-cover"}`}
              />
              {/* Active indicator dot */}
              {idx === currentIndex && (
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#ffcc29] rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
